/**
 * 学校迁移交割期前端处理
 * 20100 冻结中 / 20101 已迁移至新平台
 * done_jump_mode: 0=弹窗引导（默认）1=自动跳转
 */

export interface MigrationInfo {
  phase?: number
  cutover_start?: number
  cutover_end?: number
  notice_text?: string
  new_platform_url?: string
  new_platform_url_mobile?: string
  new_platform_url_jigou?: string
  done_jump_mode?: number
}

export const MIGRATION_CODE_FREEZE = 20100
export const MIGRATION_CODE_DONE = 20101

/** 机构端优先 jigou 地址，未配置则回退 PC 地址 */
export function resolveJigouPlatformUrl(info: MigrationInfo | null | undefined): string {
  if (!info) return ''
  return String(info.new_platform_url_jigou || info.new_platform_url || '')
}

export function isAutoJumpMode(info: MigrationInfo | null | undefined): boolean {
  return Number(info && info.done_jump_mode) === 1
}

export function isMigrationCode(code: number | string | undefined): boolean {
  const n = Number(code)
  return n === MIGRATION_CODE_FREEZE || n === MIGRATION_CODE_DONE
}

export function handleMigrationResponse(res: {
  code?: number | string
  data?: MigrationInfo
}): boolean {
  if (!isMigrationCode(res && res.code)) {
    return false
  }
  const data = res.data || {}
  try {
    sessionStorage.setItem('migrationInfo', JSON.stringify(data))
  } catch (e) {
    /* ignore */
  }
  const url = resolveJigouPlatformUrl(data)
  if (Number(res.code) === MIGRATION_CODE_DONE && isAutoJumpMode(data) && url) {
    window.location.href = url
  } else {
    window.location.href = `${window.location.origin}/#/migration`
  }
  return true
}

export function applyMigrationFromUserInfo(migration: MigrationInfo | null | undefined): void {
  if (!migration || !migration.phase) {
    return
  }
  const phase = Number(migration.phase)
  if (phase >= 2) {
    try {
      sessionStorage.setItem('migrationInfo', JSON.stringify(migration))
    } catch (e) {
      /* ignore */
    }
    const url = resolveJigouPlatformUrl(migration)
    if (phase >= 3 && isAutoJumpMode(migration) && url) {
      window.location.href = url
    } else {
      window.location.href = `${window.location.origin}/#/migration`
    }
    return
  }
  if (phase === 1) {
    ;(window as any).schoolConfig = (window as any).schoolConfig || {}
    ;(window as any).schoolConfig.migration = migration
    window.dispatchEvent(new Event('migrationNotice'))
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** 公告文案白名单清洗（无 DOMPurify 时的轻量实现） */
function sanitizeNoticeHtml(html: string): string {
  let s = html.replace(/<(?!\/?(?:p|br|span|font|b|strong)\b)[^>]*>/gi, '')
  s = s.replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
  s = s.replace(/javascript\s*:/gi, '')
  s = s.replace(/\sstyle\s*=\s*("([^"]*)"|'([^']*)')/gi, (_m, _q, d1, d2) => {
    const style = d1 || d2 || ''
    const cm = style.match(/color\s*:\s*([^;]+)/i)
    if (!cm) return ''
    const color = String(cm[1]).trim()
    if (/^(#[0-9a-fA-F]{3,8}|rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|[a-zA-Z]+)$/.test(color)) {
      return ` style="color:${color}"`
    }
    return ''
  })
  s = s.replace(/\scolor\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/gi, (_m, _a, d1, d2, d3) => {
    const color = String(d1 || d2 || d3 || '').trim()
    if (/^(#[0-9a-fA-F]{3,8}|rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|[a-zA-Z]+)$/.test(color)) {
      return ` color="${color}"`
    }
    return ''
  })
  return s
}

/** 公告文案转安全 HTML：支持标红；纯文本兼容换行 */
export function formatNoticeHtml(raw: string, fallback = ''): string {
  let text = String(raw || '').trim()
  if (!text) {
    text = String(fallback || '').trim()
  }
  if (!text) {
    return ''
  }
  if (!/<\/?[a-z][\s\S]*>/i.test(text)) {
    return escapeHtml(text).replace(/\n/g, '<br>')
  }
  return sanitizeNoticeHtml(text)
}

/** 横幅等行内场景：去掉块级 p */
export function formatNoticeHtmlInline(raw: string, fallback = ''): string {
  return formatNoticeHtml(raw, fallback).replace(/<\/?p[^>]*>/gi, '')
}

/** 纯文本（toast / 通知条） */
export function stripNoticeText(raw: string): string {
  return String(raw || '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}
